import i18n from 'i18next';
import { useSyncExternalStore } from 'react';

import defaultWords from './common-unboost.default.json';

export const BLOCKED_BOOST_WORDS_CONFIG_KEY = 'common_unboost';
const CACHE_KEY = 'ava.blockedBoostWords.v1';

// Common filler words can distort recognition when boosted. Reject new entries at
// LiveKit write boundary instead of changing live vocabulary by language or deleting
// existing user data. Match entire phrases so e.g. "The Hague" remains allowed.
export const normalizeBoostEntry = (word: string): string =>
  word
    .normalize('NFKC')
    .toLowerCase()
    .replace(/[‘’]/g, "'")
    .trim()
    .replace(/^\p{P}+|\p{P}+$/gu, '')
    .trim()
    .replace(/\s+/g, ' ');

export const parseBlockedBoostWords = (raw: string | undefined): string[] | undefined => {
  if (raw == null || raw.trim() === '') return undefined;
  try {
    const value: unknown = JSON.parse(raw);
    if (!Array.isArray(value) || !value.every((word) => typeof word === 'string')) return undefined;
    return [...new Set(value.map(normalizeBoostEntry).filter(Boolean))];
  } catch {
    return undefined;
  }
};

// Preserve the supplied list verbatim in JSON; normalize and deduplicate only at load time.
const loadBlockedBoostWords = (): string[] => {
  try {
    return (
      parseBlockedBoostWords(localStorage.getItem(CACHE_KEY) ?? undefined) ?? [
        ...new Set(defaultWords.map(normalizeBoostEntry).filter(Boolean)),
      ]
    );
  } catch {
    return [...new Set(defaultWords.map(normalizeBoostEntry).filter(Boolean))];
  }
};

let blockedWords = loadBlockedBoostWords();
const listeners = new Set<() => void>();
const getBlockedWords = () => blockedWords;
const subscribeToBlockedWords = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const filterAllowedBoostWords = (words: string[], blocked: readonly string[] = blockedWords): string[] => {
  const blockedKeys = new Set(blocked);
  return words.filter((word) => !blockedKeys.has(normalizeBoostEntry(word)));
};

// Keep LiveKit suggestions current when Remote Config finishes loading.
export const useAllowedBoostWords = (words: string[]): string[] => {
  const currentBlockedWords = useSyncExternalStore(subscribeToBlockedWords, getBlockedWords, getBlockedWords);
  return filterAllowedBoostWords(words, currentBlockedWords);
};

export const applyBlockedBoostWordsConfig = (raw: string | undefined): void => {
  const parsed = parseBlockedBoostWords(raw);
  if (parsed == null) return;
  // A valid remote value replaces the fallback, including an intentional []. Keep
  // the last valid value offline; malformed config must not erase it.
  blockedWords = parsed;
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(parsed));
  } catch {
    // Storage may be unavailable in private webviews; the in-memory value still works.
  }
  listeners.forEach((listener) => listener());
};

export class BlockedBoostWordsError extends Error {
  constructor(words: string[]) {
    super(i18n.t<string>('boost.error.commonWords', { words: words.join(', ') }));
    this.name = 'BlockedBoostWordsError';
  }
}

export const assertBoostWordsAllowed = (words: string[]): void => {
  const blocked = new Set(blockedWords);
  const matches = [
    ...new Set(words.filter((word) => blocked.has(normalizeBoostEntry(word))).map((word) => word.trim())),
  ];
  // Reject the complete batch: callers mark every submitted entry saved on success.
  if (matches.length > 0) throw new BlockedBoostWordsError(matches);
};

export const getBoostSaveErrorMessage = (error: unknown, fallback: string): string =>
  error instanceof BlockedBoostWordsError ? error.message : fallback;
